import { useMemo } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import gql from '@zenbusiness/application-commons-utils/gql';

import { GET_BUSINESS_LICENSE_ORDERS_QUERY } from '#client/queries/businessLicenseOrders';

export const GET_BLR_ORDER_TASKS_QUERY = gql`
  query BlrOrderTasksByBusinessEntity($businessEntityUuid: UUID!) {
    orderTasksByBusinessEntity(businessEntityUuid: $businessEntityUuid) {
      uuid
      type
      status
    }
  }
`;

const BLR_TASK_TYPE = 'business_license_report';
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);

export function useBlrRepurchaseReadiness({ enabled = false } = {}) {
  const { businessEntityUuid } = useBusinessEntity();

  const {
    data: ordersData,
    loading: ordersLoading,
    errors: ordersErrors
  } = useQuery(GET_BUSINESS_LICENSE_ORDERS_QUERY, {
    noThrow: true,
    skip: !enabled,
    variables: { businessEntity: businessEntityUuid }
  });

  const {
    data: tasksData,
    loading: tasksLoading,
    errors: tasksErrors
  } = useQuery(GET_BLR_ORDER_TASKS_QUERY, {
    noThrow: true,
    skip: !enabled,
    variables: { businessEntityUuid }
  });

  useError(ordersErrors);
  useError(tasksErrors);

  const allBlrOrdersComplete = useMemo(() => {
    if (!enabled) return false;

    const orders = ordersData?.businessLicenseOrders?.orders || [];
    const allTasks = tasksData?.orderTasksByBusinessEntity || [];

    const blrTasks = allTasks.filter(
      (t) => t.type === BLR_TASK_TYPE && !CANCELLED_STATUSES.has(t.status)
    );

    // Merge: add tasks that don't yet have a corresponding license order resource.
    // These represent in-flight orders (questionnaire not complete, pending Avalara processing, etc.)
    const tasksWithoutOrders = blrTasks.filter(
      (task) => !orders.some((order) => order.orderTaskUuid === task.uuid)
    ).map((task) => ({
      orderTaskUuid: task.uuid,
      type: 'report',
      status: task.status
    }));

    const mergedReports = [
      ...orders.filter((o) => o.type === 'report'),
      ...tasksWithoutOrders
    ];

    return mergedReports.length > 0
      && mergedReports.every((order) => order.status === 'complete');
  }, [enabled, ordersData, tasksData]);

  return {
    allBlrOrdersComplete,
    loading: ordersLoading || tasksLoading
  };
}
